import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from 'react';
import { UserContext } from './ContextStore';
import { loadWorkspace, saveWorkspace } from './workspace/workspacePersistence';
import { publishUnsavedWork, unsavedWorkOf } from './workspace/unsavedWork';
import {
  workspaceReducer,
  initialWorkspaceState,
  openPedigreeAction,
  openPersonAction,
  setActiveAction,
  pinSessionAction,
  markEditedAction,
  setLockAction,
  setUnsavedSectionsAction,
  updateViewStateAction,
  renameSessionAction,
  setProbandNameAction,
  requestReleaseAction,
  markInaccessibleAction,
  closeSessionAction,
  closeOthersAction,
  closeAllAction,
  reorderTabsAction,
  getActiveSession
} from './workspace/workspaceReducer';

// App-level store for the multi-pedigree-tabs workspace: the set of open
// pedigrees, tab order, the active tab and the (single) preview tab. Mounted high
// in the tree (Dashboard, inside UserContext) so the open set survives navigating
// between the pedigree list, a report and back — the preview tab is only recycled
// when a DIFFERENT pedigree is opened, per the agreed UX. Modelled on
// AnnouncementsContext.js (context object + provider component + a hook).
//
// The reducer is pure; this provider is the impure edge that mints session ids and
// timestamps. useWorkspace() returns null when there is no provider (feature flag
// off), so callers that also work in single-pedigree mode must null-check.

const WorkspaceContext = createContext(null);

// Actions only, and stable for the life of the provider. See useWorkspaceActions.
const WorkspaceActionsContext = createContext(null);

export function WorkspaceProvider({ children }) {
  // The open set is scoped to the signed-in user (TGCLOUD-1618): sessionStorage
  // survives logout → login in the same browser tab, so without this the next user
  // inherits the previous one's tabs — usually another tenant's families, which
  // then 403. Read synchronously below, so the very first restore is already
  // owner-checked. `user` is populated before this provider mounts (AdminPanel
  // renders the app shell only once `user.email` is present), but a missing id is
  // still handled as a refusal rather than a free pass.
  // `UserContext` defaults to null, and the workspace tests render this provider
  // standalone — so tolerate no provider rather than throwing on the destructure.
  const { user } = useContext(UserContext) || {};
  const ownerId = user?._id ? String(user._id) : null;

  // Restore the open tab SET synchronously (lazy init), so it is present before
  // the host mounts and re-opens the URL's family (which then just de-dupes). The
  // order matters: RESTORE replaces the whole state, so restoring later would
  // discard a session the host had already opened.
  const [state, dispatch] = useReducer(
    workspaceReducer,
    initialWorkspaceState,
    (base) => {
      const restored = loadWorkspace(ownerId);
      return restored
        ? {
            sessions: restored.sessions,
            activeSessionId: restored.activeSessionId,
            previewSessionId: restored.previewSessionId
          }
        : base;
    }
  );

  // Persist the open set on every change (best-effort; sessionStorage), stamped
  // with its owner. A save with no owner writes nothing rather than a snapshot
  // that could never be restored.
  useEffect(() => {
    saveWorkspace(state, ownerId);
  }, [state, ownerId]);

  // Tell the app bar, which renders above this provider, which tabs hold
  // unsaved sidebar edits, so Log Out and the user-group switcher can ask
  // before tearing the workspace down. Nothing unsaved once it has gone.
  useEffect(() => {
    publishUnsavedWork(unsavedWorkOf(state));
  }, [state]);
  useEffect(() => () => publishUnsavedWork([]), []);

  // Monotonic, DOM-id-safe session id (also used as the pedigree instanceId that
  // namespaces SVG element ids). Alphanumeric + hyphen only.
  const counterRef = useRef(0);
  const mintId = useCallback(
    () => `ws${Date.now().toString(36)}-${counterRef.current++}`,
    []
  );

  const openPedigree = useCallback(
    (familyId, { title, activate = true, pinned = false } = {}) =>
      dispatch(
        openPedigreeAction({
          familyId,
          title,
          sessionId: mintId(),
          now: Date.now(),
          activate,
          pinned
        })
      ),
    [mintId]
  );

  // TGCLOUD-1669 — open a PERSON as a tab. `familyId` is the chart the person
  // belongs to, carried for the tab's "Open pedigree" action and for scoping;
  // the tab's identity is the memberId, so a person and their family's pedigree
  // are two tabs and either can be open without the other.
  const openPerson = useCallback(
    (memberId, { familyId, title, activate = true, pinned = false } = {}) =>
      dispatch(
        openPersonAction({
          memberId,
          familyId,
          title,
          sessionId: mintId(),
          now: Date.now(),
          activate,
          pinned
        })
      ),
    [mintId]
  );

  const setActive = useCallback(
    (sessionId) => dispatch(setActiveAction(sessionId, Date.now())),
    []
  );
  const pinSession = useCallback(
    (sessionId) => dispatch(pinSessionAction(sessionId)),
    []
  );
  const markEdited = useCallback(
    (sessionId) => dispatch(markEditedAction(sessionId)),
    []
  );
  const setLock = useCallback(
    (sessionId, lock) => dispatch(setLockAction(sessionId, lock)),
    []
  );
  // The tab's sidebar reports its unsaved sections (TGCLOUD-1839). Called by
  // the mount on every change and with [] on unmount; see the reducer.
  const setUnsavedSections = useCallback(
    (sessionId, sections, count) =>
      dispatch(setUnsavedSectionsAction(sessionId, sections, count)),
    []
  );
  const updateViewState = useCallback(
    (sessionId, patch) => dispatch(updateViewStateAction(sessionId, patch)),
    []
  );
  const renameSession = useCallback(
    (familyId, title) => dispatch(renameSessionAction(familyId, title)),
    []
  );
  const setProbandName = useCallback(
    (familyId, probandName) =>
      dispatch(setProbandNameAction(familyId, probandName)),
    []
  );
  const requestRelease = useCallback(
    (familyId) => dispatch(requestReleaseAction(familyId)),
    []
  );
  const markInaccessible = useCallback(
    (familyId) => dispatch(markInaccessibleAction(familyId)),
    []
  );
  const closeSession = useCallback(
    (sessionId) => dispatch(closeSessionAction(sessionId)),
    []
  );
  const closeOthers = useCallback(
    (sessionId) => dispatch(closeOthersAction(sessionId)),
    []
  );
  const closeAll = useCallback(() => dispatch(closeAllAction()), []);
  const reorderTabs = useCallback(
    (order) => dispatch(reorderTabsAction(order)),
    []
  );

  // TGCLOUD-1775 — the ACTIONS are their own context, and separate from the
  // state on purpose.
  //
  // Every action below is a useCallback with a stable identity, but they used
  // to be handed out inside a value memoised on `state`. So a component that
  // wanted nothing but `openPedigree` re-rendered on every activation, every
  // lock badge and every view-state patch, and context propagation bypasses
  // parent memo bailouts, so its whole subtree went with it. Measured: a tab
  // switch committed all four open tabs.
  //
  // A consumer that only acts now subscribes to something that never changes.
  const actions = useMemo(
    () => ({
      openPedigree,
      openPerson,
      setActive,
      pinSession,
      markEdited,
      setLock,
      setUnsavedSections,
      updateViewState,
      renameSession,
      setProbandName,
      requestRelease,
      markInaccessible,
      closeSession,
      closeOthers,
      closeAll,
      reorderTabs
    }),
    [
      openPedigree,
      openPerson,
      setActive,
      pinSession,
      markEdited,
      setLock,
      setUnsavedSections,
      updateViewState,
      renameSession,
      setProbandName,
      requestRelease,
      markInaccessible,
      closeSession,
      closeOthers,
      closeAll,
      reorderTabs
    ]
  );

  // The combined value, unchanged, so every existing useWorkspace() caller
  // keeps working exactly as before. Consumers that re-render for no reason are
  // migrated to useWorkspaceActions() one at a time, on evidence.
  const value = useMemo(
    () => ({
      state,
      activeSession: getActiveSession(state),
      ...actions
    }),
    [state, actions]
  );

  return (
    <WorkspaceActionsContext.Provider value={actions}>
      <WorkspaceContext.Provider value={value}>
        {children}
      </WorkspaceContext.Provider>
    </WorkspaceActionsContext.Provider>
  );
}

// Returns the workspace API, or null when there is no provider (feature flag off).
export const useWorkspace = () => useContext(WorkspaceContext);

/**
 * Just the actions, and nothing that changes.
 *
 * Prefer this wherever a component only needs to DO something to the workspace.
 * `useWorkspace()` hands back the state as well, so it re-renders its consumer
 * on every activation, lock badge and view-state patch, and takes the subtree
 * with it.
 */
export const useWorkspaceActions = () => useContext(WorkspaceActionsContext);
